import { emptyLogin, isLogin, type Login } from '../models/auth.model'
import type AccessToken from '../models/access-token.interface'
import { isAccessToken } from '../models/access-token.interface'
import { InfoService } from '@/modules/core/services/info.service'
import type { LoginResponse } from '../models/response.interface'

import { decryptJSON, encryptJSON } from '../../core/services/crypto.service'
import AuthApiService from '../services/auth-api.service'

import * as AuthStoreConstants from '../constants/auth-store-constants'
import * as AuthLocalStorageConstants from '../constants/auth-local-storage-constants'
import { env } from '@/config/env.ts'

const isProduction = import.meta.env.MODE === 'production'

const authApiService = new AuthApiService()

function safeDecryptAccessToken(): AccessToken | null {
  const encrypted = localStorage.getItem(AuthLocalStorageConstants.ACCESS_TOKEN)
  if (!encrypted) return null
  try {
    const value = decryptJSON<unknown>(encrypted, env.accessTokenSecretKey)
    if (isAccessToken(value)) return value
    logWarn('AuthStore', 'Malformed access token in localStorage — clearing')
  } catch {
    logWarn('AuthStore', 'Failed to decrypt access token — clearing')
  }
  localStorage.removeItem(AuthLocalStorageConstants.ACCESS_TOKEN)
  return null
}

function safeDecryptPendingLogin(): Login | null {
  const encrypted = localStorage.getItem(AuthLocalStorageConstants.PENDING_LOGIN)
  if (!encrypted) return null
  try {
    const value = decryptJSON<unknown>(encrypted, env.pendingLoginSecretKey)
    if (isLogin(value)) return value
    logWarn('AuthStore', 'Malformed pending login in localStorage — clearing')
  } catch {
    logWarn('AuthStore', 'Failed to decrypt pending login — clearing')
  }
  localStorage.removeItem(AuthLocalStorageConstants.PENDING_LOGIN)
  return null
}

export const useAuthStore = defineStore(AuthStoreConstants.AUTH, () => {
  const { handleError } = useErrorHandler()
  const { showToastError } = useToastComposable()

  const appUserStore = useAppUserStore()
  const firebaseStore = useFirebaseStore()

  const returnUrl = ref<string>()
  const mAccessToken = ref<AccessToken | null>(safeDecryptAccessToken())

  const mLogin = ref<Login>({ ...emptyLogin })
  const mPendingLogin = ref<Login | null>(safeDecryptPendingLogin())
  const mEmailError = ref<string | null>(null)
  const mPasswordError = ref<string | null>(null)

  const isSubmitting = ref<boolean>(false)

  const isLoggedIn = computed<boolean>(() => mAccessToken.value != null)

  /**
   * Validates form inputs
   */
  const validateForm = (): boolean => {
    let isValid = true

    if (mLogin.value.email.length < 2) {
      mEmailError.value = 'Email is required'
      isValid = false
    } else {
      mEmailError.value = null
    }

    if (mLogin.value.password.length < 1) {
      mPasswordError.value = 'Password is required'
      isValid = false
    } else {
      mPasswordError.value = null
    }

    return isValid
  }

  /**
   * Collects device, location, and browser information
   */
  const collectLoginMetadata = async () => {
    const [deviceInfo,  browserInfo] = await Promise.all([
      InfoService.collectDeviceInfo(),
      // InfoService.collectLocationInfo(),
      InfoService.collectBrowserInfo(),
    ])

    mLogin.value.deviceInfo = deviceInfo
    // mLogin.value.locationInfo = locationInfo
    mLogin.value.browserInfo = browserInfo
    mLogin.value.timestamp = new Date().toISOString()
    mLogin.value.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  /**
   * Handles successful login — returns the route name for the caller to navigate to.
   */
  const handleLoginSuccess = (response: LoginResponse) => {
    mAccessToken.value = response.accessToken
    appUserStore.setAppUser(response.user, response.permissions)
    firebaseStore.setToken()
  }

  const saveAccessToken = () => {
    localStorage.setItem(
      AuthLocalStorageConstants.ACCESS_TOKEN,
      encryptJSON(mAccessToken.value, env.accessTokenSecretKey)
    )
    mLogin.value = emptyLogin
    // showToastSuccess(`Welcome back, ${response.user.firstName} ${response.user.lastName}`)
  }

  /**
   * Main login function with reCAPTCHA v3.
   * Returns the route name to navigate to on success, or null on failure.
   */
  const onLogin = async (recaptchaToken?: string | null): Promise<'auth.verification' | null> => {
    if (!validateForm()) return null

    if (isProduction && !recaptchaToken) {
      showToastError('Security verification failed. Please try again.')
      return null
    }

    isSubmitting.value = true
    let redirectTo: 'auth.verification' | null = null

    try {
      if (recaptchaToken) mLogin.value.recaptchaToken = recaptchaToken

      // await collectLoginMetadata()

      const result = await authApiService.login(mLogin.value)

      result.fold(
        value => {
          mPendingLogin.value = { ...mLogin.value }
          localStorage.setItem(
            AuthLocalStorageConstants.PENDING_LOGIN,
            encryptJSON(mPendingLogin.value, env.pendingLoginSecretKey)
          )
          handleLoginSuccess(value as LoginResponse)
          redirectTo = 'auth.verification'
        },
        error => handleError(error)
      )
    } catch (error) {
      logWarn('AuthStore::onLogin', `Unexpected error: ${error}`)
      showToastError('An unexpected error occurred. Please try again.')
    } finally {
      isSubmitting.value = false
    }

    return redirectTo
  }

  /**
   * Logs out the user. Callers are responsible for navigating to /auth/login.
   */
  const logout = () => {
    appUserStore.removeAppUser()
    mAccessToken.value = null
    localStorage.removeItem(AuthLocalStorageConstants.ACCESS_TOKEN)
    mPendingLogin.value = null
    localStorage.removeItem(AuthLocalStorageConstants.PENDING_LOGIN)
    firebaseStore.removeToken()
  }

  /**
   * Resets the login form
   */
  const resetLoginForm = () => {
    mLogin.value = { ...emptyLogin }
    mEmailError.value = null
    mPasswordError.value = null
  }

  return {
    // State
    login: mLogin,
    emailError: mEmailError,
    passwordError: mPasswordError,
    returnUrl,
    isLoggedIn,
    accessToken: mAccessToken,
    isSubmitting,

    // Actions
    onLogin,
    logout,
    resetLoginForm,
    saveAccessToken,
  }
})
